import { useMemo, useState } from 'react';
import { AutocompleteSelect } from '@/components/ui/autocomplete-select';
import { cn } from '@/lib/utils';
import { useCategorias } from '@/contabilidad/hooks/useCategorias';

interface CategoriaOption {
  id: string;
  nombre: string;
}

interface Props {
  tipo: 'Ingreso' | 'Egreso';
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
}

export function CategoriaAutocomplete({ tipo, value, onChange, error }: Props) {
  const { categorias, isLoading, createCategoria, isCreating } = useCategorias(tipo);
  const [inputValue, setInputValue] = useState('');

  const normalizedSearch = inputValue.trim().toLowerCase();

  const options = useMemo<CategoriaOption[]>(() => {
    const filtered = normalizedSearch
      ? categorias.filter((c) => c.nombre.toLowerCase().includes(normalizedSearch))
      : categorias;
    return filtered.map((c) => ({ id: c.nombre, nombre: c.nombre }));
  }, [categorias, normalizedSearch]);

  const canCreate = normalizedSearch.length > 0
    && !categorias.some((c) => c.nombre.toLowerCase() === normalizedSearch);

  const selectedOption: CategoriaOption | null = value ? { id: value, nombre: value } : null;

  async function handleCreate(nombre: string) {
    const nueva = await createCategoria({ nombre, tipo });
    onChange(nueva.nombre);
    setInputValue('');
  }

  return (
    <AutocompleteSelect<CategoriaOption>
      mode="single"
      value={selectedOption}
      onChange={(option) => onChange(option?.nombre ?? '')}
      onClear={() => onChange('')}
      options={options}
      getOptionLabel={(option) => option.nombre}
      placeholder="Seleccionar categoría..."
      ariaLabel="Categoría"
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      isLoading={isLoading}
      emptyMessage="No se encontraron categorías."
      createOptionLabel={canCreate ? `Crear "${inputValue.trim()}"` : undefined}
      onCreate={canCreate ? handleCreate : undefined}
      isCreating={isCreating}
      className={cn(error && '[&_button[role=combobox]]:border-rose-500')}
    />
  );
}
